import React, { useState, useRef, useEffect, useContext } from 'react';
import { userManager } from '@teko/auth';
import { Partner } from './types.es.js';
import { cleanObject, extractFromPath } from '@teko/utils';
import { logger } from '@teko/log';
import { SubTitle, Body, ModalBody, Checkbox, styled, Modal } from '@teko/ds-desktop';
import { getTrackingData } from '@teko/tracking';
import { isSacombankmbPartner, userConfirmedPolicy, checkShowConfirmPolicyModal } from './helper.es.js';
import { UpdatePhoneNumberModal } from './components/UpdatePhoneNumberModal/UpdatePhoneNumberModal.es.js';

var __defProp = Object.defineProperty;
var __defProps = Object.defineProperties;
var __getOwnPropDescs = Object.getOwnPropertyDescriptors;
var __getOwnPropSymbols = Object.getOwnPropertySymbols;
var __hasOwnProp = Object.prototype.hasOwnProperty;
var __propIsEnum = Object.prototype.propertyIsEnumerable;
var __defNormalProp = (obj, key, value) => key in obj ? __defProp(obj, key, { enumerable: true, configurable: true, writable: true, value }) : obj[key] = value;
var __spreadValues = (a, b) => {
  for (var prop in b || (b = {}))
    if (__hasOwnProp.call(b, prop))
      __defNormalProp(a, prop, b[prop]);
  if (__getOwnPropSymbols)
    for (var prop of __getOwnPropSymbols(b)) {
      if (__propIsEnum.call(b, prop))
        __defNormalProp(a, prop, b[prop]);
    }
  return a;
};
var __spreadProps = (a, b) => __defProps(a, __getOwnPropDescs(b));
var __objRest = (source, exclude) => {
  var target = {};
  for (var prop in source)
    if (__hasOwnProp.call(source, prop) && exclude.indexOf(prop) < 0)
      target[prop] = source[prop];
  if (source != null && __getOwnPropSymbols)
    for (var prop of __getOwnPropSymbols(source)) {
      if (exclude.indexOf(prop) < 0 && __propIsEnum.call(source, prop))
        target[prop] = source[prop];
    }
  return target;
};
const IBANKING_LOGIN_DATA = "ibanking_login_data";
const FEDERATION_PARTNER = "federation";
const CONSUMERAPP_PARTNER = "consumerapp";
const LOCALSTORAGE_REFRESH_TOKEN = "REFRESH_TOKEN";
const LOCALSTORAGE_BACK_APP_URL = "BACK_APP_URL";
const RELOAD_DUE_TO_MISSING_ACCESS_TOKEN = "reload-due-to-missing-access-token";
const mergeInfo = (login, profile) => {
  return __spreadProps(__spreadValues(__spreadValues({}, login), profile), {
    userId: login.userId || profile.userId,
    name: profile.name || login.name,
    telephone: profile.telephone || login.phone,
    avatar: login.picture || profile.avatar
  });
};
const AuthContext = React.createContext(null);
const AuthContextProvider = ({
  children,
  userService,
  authService,
  partner,
  scopes,
  router,
  showConfirmModalAfterLogin = false,
  forceUpdatePhoneNumber = false,
  isNeedToUpdateProfile = false,
  otpRequestCountDown
}) => {
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState(null);
  const [show, setShow] = useState(false);
  const [showUpdatePhoneNumberModal, setShowUpdatePhoneNumberModal] = useState(false);
  const [showProfileModal, setShowProfileModal] = useState(false);
  const [reCaptchaConfig, setReCaptchaConfig] = useState({
    enabled: false,
    site_key: ""
  });
  const lastRenewTokenTime = useRef(0);
  const [agreePrivacyAndPolicy, setAgreePrivacyAndPolicy] = useState(isSacombankmbPartner(partner) ? true : false);
  const updateProfile = async (profileInput) => {
    const profile = await userService.updateProfile(profileInput);
    const updatedProfile = __spreadValues(__spreadValues({}, user), cleanObject(profile));
    setUser(updatedProfile);
    return updatedProfile;
  };
  const fetchProfile = async (params = {}, infoToMerged) => {
    var _a;
    if (!!userManager.getAccessToken()) {
      try {
        setLoading(true);
        const profile = await userService.myProfile(params);
        setUser(mergeInfo(infoToMerged || authService.getLoginInfo() || {}, profile));
        if (forceUpdatePhoneNumber && !(infoToMerged == null ? void 0 : infoToMerged.phone) && !((_a = authService.getLoginInfo()) == null ? void 0 : _a.phone) && !profile.telephone) {
          setShowUpdatePhoneNumberModal(true);
        }
      } catch (e) {
        setUser(null);
        logger.error(e);
      } finally {
        setLoading(false);
      }
    }
  };
  const _loginWithConsumerApp = async (userInfo) => {
    const loginInfo = await authService.loginWithTerra(userInfo);
    await fetchProfile({}, loginInfo);
  };
  const _loginWithIBanking = async (queries) => {
    const isSacombankMb = partner === "sacombankmb";
    if (isSacombankMb) {
      queries.signature = queries.signature || queries.data;
      queries.partner_code = queries.partner_code || "12901";
    }
    const body = await authService.loginWithIBanking(queries);
    const { partner_code, token: partner_token } = body.ibanking_info || {};
    await fetchProfile(partner_code && partner_token ? {
      partnerCode: partner_code,
      partnerToken: partner_token
    } : {});
  };
  const _loginWithFederation = async (queries) => {
    const terraIdToken = queries.terraIdToken;
    const authorizer = queries.authorizer;
    if (!terraIdToken || !authorizer) {
      throw new Error("Missing federarion login config");
    }
    const { refresh_token } = await authService.loginWithFederation(terraIdToken, authorizer);
    localStorage == null ? void 0 : localStorage.setItem(LOCALSTORAGE_REFRESH_TOKEN, refresh_token);
    await fetchProfile({});
  };
  const _refreshToken = async () => {
    const refreshToken = localStorage == null ? void 0 : localStorage.getItem(LOCALSTORAGE_REFRESH_TOKEN);
    if (!refreshToken) {
      return;
    }
    const { refresh_token } = await authService.refreshToken(refreshToken);
    localStorage == null ? void 0 : localStorage.setItem(LOCALSTORAGE_REFRESH_TOKEN, refresh_token);
    await fetchProfile({});
  };
  const _getPartnerType = (partnerType) => {
    switch (partnerType) {
      case CONSUMERAPP_PARTNER:
        return Partner.CONSUMERAPP;
      case FEDERATION_PARTNER:
        return Partner.FEDERATION;
      default:
        return Partner.IBANKING;
    }
  };
  useEffect(() => {
    window.addEventListener("storage", async ({ key, newValue, oldValue }) => {
      if (key == null ? void 0 : key.includes("tekoid.user")) {
        if (!newValue && oldValue) {
          setUser(null);
        } else if (newValue && !oldValue) {
          await fetchProfile();
        }
      }
    });
    return () => {
      window.removeEventListener("storage", () => null);
    };
  }, [user == null ? void 0 : user.userId]);
  const loginWithPartner = async (queries, autoRenew = true, autoRedirect = true) => {
    const _a = queries, { redirect_uri, backAppUrl } = _a, storeData = __objRest(_a, ["redirect_uri", "backAppUrl"]);
    sessionStorage.setItem(IBANKING_LOGIN_DATA, JSON.stringify(storeData));
    if (backAppUrl) {
      localStorage.setItem(LOCALSTORAGE_BACK_APP_URL, backAppUrl);
    }
    const _loginWithPartner = async () => {
      var _a2;
      try {
        const partnerType = _getPartnerType(queries.partnerType);
        switch (partnerType) {
          case Partner.FEDERATION: {
            await _loginWithFederation(queries);
            break;
          }
          case Partner.IBANKING: {
            await _loginWithIBanking(queries);
            break;
          }
          case Partner.CONSUMERAPP: {
            await _loginWithConsumerApp(queries.userInfo);
            break;
          }
        }
        if (router) {
          if (queries == null ? void 0 : queries.redirect_uri) {
            const extractLink = extractFromPath(queries.redirect_uri);
            typeof extractLink === "object" ? router.push(extractLink, queries.redirect_uri) : router.push(extractLink);
            return;
          }
          if (autoRedirect) {
            if (partnerType === Partner.IBANKING && checkShowConfirmPolicyModal((_a2 = authService.getLoginInfo()) == null ? void 0 : _a2.userId) && (!isSacombankmbPartner(partner) && !agreePrivacyAndPolicy && showConfirmModalAfterLogin || isSacombankmbPartner(partner))) {
              return setShow(true);
            } else {
              router.push("/");
            }
          }
        } else {
          logger.error("auth-react: router is not set");
        }
      } catch (e) {
        logger.error(e);
        window.track("customEvent", "loginWithPartnerFailed", {
          action: "loginWithPartnerFailed",
          category: "customer",
          label: JSON.stringify({
            errorStacktrace: (e == null ? void 0 : e.stacktrace) || "unknown",
            errorMessage: (e == null ? void 0 : e.message) || "unknown"
          })
        });
        if (window.location.pathname.includes("/login"))
          alert("\u0110\u0103ng nh\u1EADp th\u1EA5t b\u1EA1i!");
      }
    };
    await _loginWithPartner();
    if (autoRenew) {
      const startTime = new Date().getTime();
      lastRenewTokenTime.current = startTime;
      setInterval(async () => {
        const now = new Date().getTime();
        const diff = now - lastRenewTokenTime.current;
        if (diff > 3e6) {
          lastRenewTokenTime.current = now;
          const partnerType = _getPartnerType(queries.partnerType);
          switch (partnerType) {
            case Partner.FEDERATION: {
              await _refreshToken();
              break;
            }
            case Partner.IBANKING: {
              await _loginWithPartner();
              break;
            }
          }
        }
      }, 3e3);
    }
  };
  const initIam = async () => {
    if (partner)
      return;
    await authService.initialize(scopes);
    sessionStorage.setItem(RELOAD_DUE_TO_MISSING_ACCESS_TOKEN, "0");
    const loggedInUser = authService.getLoginInfo();
    if (loggedInUser == null ? void 0 : loggedInUser.userId) {
      await fetchProfile();
    } else {
      setLoading(false);
    }
  };
  useEffect(() => {
    const queries = sessionStorage.getItem(IBANKING_LOGIN_DATA);
    if (queries) {
      const reloginWithPartner = async () => {
        try {
          const queriesObj = JSON.parse(queries);
          if (!window.location.pathname.includes("/login") && Object.keys(queriesObj).length) {
            if (queriesObj.partnerType === FEDERATION_PARTNER) {
              await _refreshToken();
            } else {
              await loginWithPartner(queriesObj, true, false);
            }
            sessionStorage.setItem(RELOAD_DUE_TO_MISSING_ACCESS_TOKEN, "0");
          }
        } catch (e) {
          logger.error("Relogin ibanking false", e);
        } finally {
          setLoading(false);
        }
      };
      reloginWithPartner();
    } else {
      initIam().finally(() => {
        setLoading(false);
      });
    }
  }, []);
  return /* @__PURE__ */ React.createElement(AuthContext.Provider, {
    value: {
      user,
      loading,
      loginWithPartner,
      authService,
      userService,
      updateProfile,
      fetchProfile,
      initIam,
      setUser,
      isShowConfirmModal: show,
      reCaptchaConfig,
      setReCaptchaConfig,
      showProfileModal,
      setShowProfileModal
    }
  }, /* @__PURE__ */ React.createElement(StyledModal, {
    visible: show,
    onCancel: () => {
      if (isSacombankmbPartner(partner)) {
        window.location.href = "/?action=backtoapp";
      } else {
        window.location.href = "/vnpayshop/backapp";
      }
    },
    onOk: () => {
      var _a;
      const userId = (_a = authService.getLoginInfo()) == null ? void 0 : _a.userId;
      if (userId) {
        userConfirmedPolicy(userId);
      }
      router == null ? void 0 : router.push("/");
      setShow(false);
    },
    okButtonProps: {
      disabled: !agreePrivacyAndPolicy
    },
    okText: "\u0110\u1ED3ng \xFD",
    cancelText: "Quay l\u1EA1i",
    showCloseIcon: false,
    title: partner !== "sacombankmb" && /* @__PURE__ */ React.createElement(React.Fragment, null, /* @__PURE__ */ React.createElement(SubTitle, {
      level: 1,
      color: "textTitle"
    }, "Th\xF4ng b\xE1o x\u1EED l\xFD d\u1EEF li\u1EC7u"), /* @__PURE__ */ React.createElement(Body, {
      level: 1,
      color: "textPrimary",
      marginTop: "s8"
    }, "Vui l\xF2ng \u0111\u1ED3ng \xFD v\u1EDBi c\xE1c \u0111i\u1EC1u kho\u1EA3n v\xE0 \u0111i\u1EC1u ki\u1EC7n d\u1ECBch v\u1EE5 \u0111\u1EC3 ti\u1EBFp t\u1EE5c")),
    centeredTitle: true
  }, /* @__PURE__ */ React.createElement(ModalBody, {
    padding: ["s0", "s0"]
  }, isSacombankmbPartner(partner) ? /* @__PURE__ */ React.createElement(React.Fragment, null, "D\u1ECBch v\u1EE5 Mua s\u1EAFm VNSHOP \u0111\u01B0\u1EE3c cung c\u1EA5p b\u1EDFi VNPAY. Chi ti\u1EBFt li\xEAn h\u1EC7 hotline VNPAY: 1900555577 \u0111\u1EC3 \u0111\u01B0\u1EE3c h\u1ED7 tr\u1EE3.") : /* @__PURE__ */ React.createElement(React.Fragment, null, /* @__PURE__ */ React.createElement(CheckboxWrapper, null, /* @__PURE__ */ React.createElement(Checkbox, __spreadProps(__spreadValues({}, getTrackingData({
    isTrackContent: true,
    regionName: "ConfirmationPrivacy",
    contentName: "checkboxAgreePrivacy"
  })), {
    checked: agreePrivacyAndPolicy,
    onChange: setAgreePrivacyAndPolicy
  }), /* @__PURE__ */ React.createElement(StyledPrivacyAndPolicyText, null, "B\u1EB1ng vi\u1EC7c \u0111\xE1nh d\u1EA5u v\xE0o \xF4 n\xE0y, t\xF4i \u0111\xE3 bi\u1EBFt r\xF5 v\xE0 \u0111\u1ED3ng \xFD to\xE0n b\u1ED9 v\u1EDBi", " ", /* @__PURE__ */ React.createElement("a", {
    href: "/chinh-sach-bao-mat-thong-tin-ca-nhan"
  }, "N\u1ED9i dung th\xF4ng b\xE1o x\u1EED l\xFD d\u1EEF li\u1EC7u c\xE1 nh\xE2n c\u1EE7a C\xF4ng ty VNIS (VNSHOP)"), " ", "li\xEAn quan t\u1EDBi vi\u1EC7c x\u1EED l\xFD d\u1EEF li\u1EC7u c\xE1 nh\xE2n c\u1EE7a t\xF4i, bao g\u1ED3m c\u1EA3 c\xE1c ho\u1EA1t \u0111\u1ED9ng ch\u1EC9nh s\u1EEDa, ti\u1EBFt l\u1ED9, x\xF3a, h\u1EE7y d\u1EEF li\u1EC7u c\xE1 nh\xE2n.")))))), /* @__PURE__ */ React.createElement(UpdatePhoneNumberModal, {
    visible: showUpdatePhoneNumberModal,
    setVisible: setShowUpdatePhoneNumberModal,
    authService,
    user,
    setUser,
    callbackConfirmOtp: () => {
      if (isNeedToUpdateProfile) {
        setShowProfileModal(true);
      }
    },
    otpRequestCountDown
  }), children);
};
function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be inside a AuthContextProvider with a state value");
  }
  return context;
}
const StyledModal = styled(Modal)`
  width: 400px;
  @media (max-width: 576px) {
    width: 80%;
  }
`;
const CheckboxWrapper = styled.div`
  text-align: center;
  .check-box {
    display: flex;
    align-items: flex-start;
    column-gap: 0.375rem;
    .checkbox-inner {
      top: 4px;
    }
    .checkbox-label {
      -webkit-line-clamp: unset;
    }
  }
`;
const StyledPrivacyAndPolicyText = styled.div`
  text-align: justify;
`;

export { AuthContextProvider, RELOAD_DUE_TO_MISSING_ACCESS_TOKEN, useAuth };
